/* eslint-disable max-len, no-underscore-dangle,no-param-reassign */
import { i18n } from 'vue-infra';
import { filter, find, map, includes, isNull, get, isNil, each, isUndefined, remove } from 'lodash';
import moment from 'moment';
import {
  USER_TYPES,
  DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE,
  DATA_LOADING_STATES,
  METRIC_NAMES,
  WIDGET_PERMISSIONS_MAP,
  WIDGET_FEATURES_MAP,
  WORKSPACE_PARTIAL_VIEW_WIDGETS,
  PRIVILEGES,
  PARTIAL_WIDGET_UNSUPPORTED_METRICS,
  FEATURES,
  WIDGET_LOGICAL_NAMES,
  PRESETS,
  WIDGET_NAMES_I18N_KEYS,
  HIDDEN_WIDGET_TABS,
  DASHBOARD_TAB_ID,
  WIDGETS_DISPLAY_ORDER,
} from '@config/Consts';
import { convertVersionToNumber, extractVersionNumber, getSiteSettingValue } from '@core/utils/utils';
import { INFRA_GETTERS, INFRA_STORE_VALUES } from '@core/store';
import { metricsConfig as METRIC_DETAILS_PER_WIDGET } from '@config/metricsConfig';

const responseTimeMetrics = [
  METRIC_NAMES.AVG_TIME_TO_RESPONSE,
  METRIC_NAMES.AVG_WAIT_TIME,
  METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE,
  METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT,
];

const _getEnableIntentsState = state => state.enableIntents;

const getSiteSetting = (state, getters, infraState, infraGetters) => (options) => {
  const isBoolean = options.isBoolean;
  const defaultValue = isBoolean ? false : options.defaultValue;
  const siteSetting = infraGetters[INFRA_GETTERS.SITE_SETTINGS]()[options.siteSettingKey];
  return getSiteSettingValue({ siteSetting, defaultValue, isBoolean });
};

const _isSupportedMetrics = (state, options) => {
  // eslint-disable-next-line no-use-before-define
  if (useWorkspacePartialWidgets(state)) {
    const unsupportedWidgetMetrics = get(PARTIAL_WIDGET_UNSUPPORTED_METRICS, options.widgetName);
    if (unsupportedWidgetMetrics && unsupportedWidgetMetrics.indexOf(options.metricName) > -1) {
      return false;
    }
  }
  return true;
};
const _getUsersByGroups = (agentsGroups, requiredGroupsIds) => {
  const groupsData = filter(agentsGroups, group => includes(requiredGroupsIds, group.id));
  const membersArray = map(groupsData, 'members');
  return membersArray.length === 0 ? [] : filter(membersArray, members => members !== null).join();
};

const getIsLPA = (state, getters, infraState, infraGetters) =>
  infraGetters[INFRA_GETTERS.GET_VALUE](INFRA_STORE_VALUES.IS_LPA);

const useWorkspacePartialWidgets = state => state.workspacePartialWidgets;
const _getTabWidgetList = state => map(filter(map(state.userSettings.widget_settings.settings, 'config'),
  widget => widget.authorized), item => ({ id: item.i, displayOrder: WIDGETS_DISPLAY_ORDER[item.i] }));
const getActiveWidgets = (state) => {
  const selectedTabId = state.userSettings.widget_settings.selectedTabId;
  return selectedTabId === DASHBOARD_TAB_ID ? undefined : [selectedTabId];
};
const getVisibleWidgets = (state) => {
  const widgets = filter(map(state.userSettings.widget_settings.settings, 'config'),
    (widget) => {
    // we show the dashboard tab when activeWidgets returns undefined
      const active = isUndefined(getActiveWidgets(state)) || includes(getActiveWidgets(state), widget.i);
      return widget.show && active && widget.authorized;
    });
  return widgets;
};
const getNewManagerApiState = state => state.useNewManagerApi;
const getFetchResponseMetricsState = state => state.fetchResponseMetrics;
const getMetricsConfiguration = state => state.accountSettings.metricsConfiguration.data;
const getWidgetValidMetrics = state => (widgetName) => {
  const widgetData = find(getMetricsConfiguration(state), widget => widget.widget_name === widgetName);
  const metrics = widgetData ? widgetData.metrics : [];
  let filterFunc;
  if (getFetchResponseMetricsState(state)) {
    filterFunc = metric => !metric.config.deleted;
  } else {
    filterFunc = metric => !metric.config.deleted &&
      !includes(responseTimeMetrics, METRIC_DETAILS_PER_WIDGET[widgetName][metric.name].metricName);
  }
  return filter(metrics, filterFunc);
};
const getWidgetParentMetrics = state => (widgetName) => {
  const metrics = getWidgetValidMetrics(state)(widgetName);
  return filter(metrics, metric => !metric.config.parentMetric);
};
const getWidgetDisplayedMetrics = state => (widgetName) => {
  const metrics = getWidgetParentMetrics(state)(widgetName);
  return filter(metrics, metric => metric.config.enabled);
};
const getWidgetMetricAttributes = state => (widgetName) => {
  const metricsAttributes = METRIC_DETAILS_PER_WIDGET[widgetName];
  const metricsConfiguration = getWidgetValidMetrics(state)(widgetName);
  const result = [];
  metricsConfiguration.forEach((metric) => {
    const metricAttributes = get(metricsAttributes, [metric.name]);
    const metricName = get(metricAttributes, 'metricName');
    if (!isNil(metricName) && !isNil(metricAttributes)) {
      const text = i18n.t(`amd:${metricAttributes.metricLabelI18nKey}`);
      // In some specific cases, the column id should be different than the metric name
      // because of the value we need to send to the API in case of sort
      const columnId = metricAttributes.metricColumnId || metricName;
      const columnHeader = !isNil(metricAttributes.metricColumnHeaderI18nKey) ? i18n.t(`amd:${metricAttributes.metricColumnHeaderI18nKey}`) : '';
      const tooltip = !isNil(metricAttributes.metricTooltipI18nKey) ? i18n.t(`amd:${metricAttributes.metricTooltipI18nKey}`) : '';
      const isColumnSortable = metricAttributes.metricColumnSortable;
      const baseColumnSize = metricAttributes.metricColumnBaseSize;
      const isVisible = metric.config.enabled && _isSupportedMetrics(state,
        { widgetName, metricName });
      const displayOrder = metric.config.displayOrder;
      const reducedDisplayOrder = metric.config.reducedDisplayOrder;
      const unitType = metricAttributes.metricUnitType;
      const valueAttr = metricAttributes.arrayItemValueAttr;
      result.push({
        metricName,
        text,
        columnId,
        columnHeader,
        tooltip,
        isColumnSortable,
        baseColumnSize,
        isVisible,
        displayOrder,
        reducedDisplayOrder,
        unitType,
        valueAttr,
      },
      );
    }
  });
  return result;
};
const getSkills = state => state.accountData.skills.data;
const getUsers = state => state.accountData.users.data;
const getAgentGroups = state => state.accountData.agentGroups.data;
const getUserManaged = state => state.accountData.userManaged.data;
const getStatusReasons = state => state.accountData.statusReasons.data;
const getBlockConsumerName = state => state.accountSettings.blockConsumerName;
const getCanConfigureManagerWorkspace = state => state.accountSettings.canConfigureManagerWorkspace;
const getAgentGroupsUpdateRequired = state => state.accountData.agentGroups.dataUpdateRequired;
const getUserSettings = state => state.userSettings;
const getGroupsFilterIds = state => state.userSettings.widget_settings.global_filters.groups;
const getSkillsFilterIds = state => state.userSettings.widget_settings.global_filters.skills;
const getGlobalFiltersUpdating = state => state.globalFiltersUpdating;
const getUserManagedGroupsIds = (state) => {
  const managedData = getUserManaged(state);
  return managedData.length > 0 ? managedData[0].agentGroups : [];
};
const getLoggedInUserId = (state, getters, infraState, infraGetters) =>
  infraGetters[INFRA_GETTERS.GET_VALUE](INFRA_STORE_VALUES.USER_ID);
const getAccountId = (state, getters, infraState, infraGetters) =>
  infraGetters[INFRA_GETTERS.GET_VALUE](INFRA_STORE_VALUES.ACCOUNT_ID);
const getLoggedInUserAgentGroupId = (state, getters, infraState, infraGetters) => {
  let agentGroupId;
  const userId = Number(getLoggedInUserId(state, getters, infraState, infraGetters));
  const loggedInUser = find(getUsers(state), user => user.id === userId);
  if (loggedInUser) {
    agentGroupId = get(loggedInUser, 'memberOf.agentGroupId');
  }
  return agentGroupId;
};
// Get all managed group ids. If groups filter was applied, ignore it.
const _getGroupIdsIgnoreFilter = (state, getters, infraState, infraGetters) => {
  let groupsIds;
  if (!useWorkspacePartialWidgets(state)) {
    groupsIds = getUserManagedGroupsIds(state);
  } else {
    groupsIds = [getLoggedInUserAgentGroupId(state, getters, infraState, infraGetters)];
  }
  return groupsIds;
};
// Get all managed groups and respect groups filter.
const getGroupsIds = (state, getters, infraState, infraGetters) => { // If no filter required for the groups, returns all managed groups ids
  let groupsIds;
  const filteredGroupsIds = getGroupsFilterIds(state);
  if (filteredGroupsIds.length === 0) {
    groupsIds = _getGroupIdsIgnoreFilter(state, getters, infraState, infraGetters);
  } else {
    groupsIds = filteredGroupsIds;
  }
  return groupsIds;
};
const getUserIds = (state, getters, infraState, infraGetters) => {
  const groupsIds = getGroupsIds(state, getters, infraState, infraGetters);
  const users = _getUsersByGroups(getAgentGroups(state), groupsIds);
  return users;
};
// Get all managed user ids of this manager. If groups filter was applied, ignore it.
const getUserIdsIgnoreFilter = (state, getters, infraState, infraGetters) => {
  const groupsIds = _getGroupIdsIgnoreFilter(state, getters, infraState, infraGetters);
  const users = _getUsersByGroups(getAgentGroups(state), groupsIds);
  return users;
};
const getUserManagedSkillsIds = (state) => {
  const managedData = getUserManaged(state);
  return managedData.length > 0 ? managedData[0].skills : [];
};
const getUserManagedSkills = state => filter(getSkills(state), skill => includes(getUserManagedSkillsIds(state), skill.id));
const getAgentGroupAllSkills = (state, getters, infraState, infraGetters) => {
  const users = filter(getUsers(state), user => includes(getUserIds(state, getters, infraState, infraGetters), user.id));
  const skillIds = new Set();
  each(users, (user) => {
    each(user.skillIds, (skillId) => {
      skillIds.add(skillId);
    });
  });
  const skills = filter(getSkills(state), skill => includes([...skillIds], skill.id));
  return skills;
};
const getSkillsFilterData = (state, getters, infraState, infraGetters) => {
  let skills = [];
  if (!useWorkspacePartialWidgets(state)) {
    skills = getUserManagedSkills(state);
  } else {
    skills = getAgentGroupAllSkills(state, getters, infraState, infraGetters);
  }
  return skills;
};
const getSkillsRevision = state => state.accountData.skills.revision;
const getUsersRevision = state => state.accountData.users.revision;
const getAgentGroupsRevision = state => state.accountData.agentGroups.revision;
const getUserManagedRevision = state => state.accountData.userManaged.revision;
const getStatusReasonsRevision = state => state.accountData.statusReasons.revision;
const getMetricsConfigurationRevision = state => state.accountSettings.metricsConfiguration.revision;
const getTimeFrameFilter = (state) => {
  const timeFrame = state.userSettings.widget_settings.global_filters.timeframe;
  if (timeFrame.absoluteHour > DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE) {
    // we need to validate the selected absoluteHour
    const startOfDay = moment().startOf('day');
    const currentTimeInHours = moment().diff(startOfDay, 'hours');
    if (timeFrame.absoluteHour > currentTimeInHours) {
      timeFrame.absoluteHour = DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE;
    }
  }
  return timeFrame;
};
// NOTE: The following method returns users as string. Currently most of APIs required the users in this format.
// We need to consider change it to returns an array after starting to work with the new APIs.
const getHumanUserIds = (state, getters, infraState, infraGetters) => {
  const humanUsers = filter(getUsers(state), user => user.userType === USER_TYPES.HUMAN);
  const humanUserIds = map(humanUsers, user => user.id.toString());
  const userIds = getUserIds(state, getters, infraState, infraGetters);
  const userIdsArray = userIds.length > 0 ? userIds.split(',') : [];
  return filter(userIdsArray, userId => includes(humanUserIds, userId));
};
const getFilteredGroupsUsersIds = (state) => {
  const groupsIds = getGroupsFilterIds(state);
  return _getUsersByGroups(getAgentGroups(state), groupsIds);
};
const getAccountDataExists = (state) => {
  const usersRevision = getUsersRevision(state);
  const skillsRevision = getSkillsRevision(state);
  const userManagedRevision = getUserManagedRevision(state);
  const agentGroupsRevision = getAgentGroupsRevision(state);
  return !isNull(usersRevision) && !isNull(skillsRevision) && !isNull(userManagedRevision)
  && !isNull(agentGroupsRevision);
};
const getNumericAmwVersion = (state) => {
  const version = state.amwVersion;
  const result = convertVersionToNumber(version);
  return result;
};
const getAmwVersion = state => (typeof state.amwVersion === 'string' ? extractVersionNumber(state.amwVersion)
  : state.amwVersion);
const getMetricsConfigurationVersion = state => state.accountSettings.metricsConfiguration.version;
const getAccountDataSubscriptionsStatus = state => state.accountData.subscriptionsStatus;
const getWidgetsSubscriptionsStatus = state => state.widgetsSubscriptionsStatus;
const getAccountDataStatus = state => state.accountData.dataStatus;
const initDataStarted = state => getAccountDataStatus((state)) !== DATA_LOADING_STATES.NONE;
const getHasPrivilege = (state, getters, infraState, infraGetters) => privilegeId =>
  !isUndefined(infraGetters[INFRA_GETTERS.USER_PRIVILEGE](privilegeId));
const getHasFeature = (state, getters, infraState, infraGetters) => feature =>
  infraGetters[INFRA_GETTERS.FEATURE_STATE](feature);
const getFeature = (state, getters, infraState, infraGetters) => feature =>
  infraGetters[INFRA_GETTERS.FEATURE](feature);
const hasLiveIntent = (state, getters, infraState, infraGetters) => {
  let liveIntentAllowed;
  const hasPortalAccessFeature = getHasFeature(state, getters, infraState, infraGetters)(FEATURES.PORTAL_ACCESS);
  const hasPortalAccessForLPAFeature = getHasFeature(state, getters, infraState, infraGetters)(FEATURES.PORTAL_ACCESS_LPA_ONLY);

  if (getIsLPA(state, getters, infraState, infraGetters)) {
    liveIntentAllowed = hasPortalAccessFeature;
  } else {
    liveIntentAllowed = hasPortalAccessFeature && !hasPortalAccessForLPAFeature;
  }
  return liveIntentAllowed && _getEnableIntentsState(state);
};

const hasLiveIntentsFilter = state => state.enableIntentsFilter;

const checkFeature = (state, getters, infraState, infraGetters, feature) => {
  let result = true;
  const featureObj = getFeature(state, getters, infraState, infraGetters)(feature);
  if (!isUndefined(featureObj)) {
    result = get(featureObj, 'value.value') || false;
  }
  return result;
};

const getWidgetPermission = (state, getters, infraState, infraGetters) => (widgetLogicalName) => {
  let allowed = true;
  if (WIDGET_PERMISSIONS_MAP[widgetLogicalName]) {
    each(WIDGET_PERMISSIONS_MAP[widgetLogicalName], (permission) => {
      allowed = allowed && getHasPrivilege(state, getters, infraState, infraGetters)(permission);
    });
  }
  if (WIDGET_FEATURES_MAP[widgetLogicalName]) {
    each(WIDGET_FEATURES_MAP[widgetLogicalName], (feature) => {
      allowed = allowed && checkFeature(state, getters, infraState, infraGetters, feature);
      // Temporary used checkFeature instead (we rollout before ac feature dictionary)
      // allowed = allowed && getHasFeature(state, getters, infraState, infraGetters)(feature);
    });
  }
  if (useWorkspacePartialWidgets(state)) {
    allowed = allowed && WORKSPACE_PARTIAL_VIEW_WIDGETS.indexOf(widgetLogicalName) > -1;
  }
  return allowed;
};
const hasAgentProfileOnly = (state, getters, infraState, infraGetters) => {
  const hasAgentProfile = getHasPrivilege(state, getters, infraState, infraGetters)(PRIVILEGES.VIEW_MESSAGING_AVAILABILITY_INFO);
  const hasAgentManagerProfile = getHasPrivilege(state, getters, infraState, infraGetters)(PRIVILEGES.SKILL_CHATS);
  return hasAgentProfile && !hasAgentManagerProfile;
};
const failedToFetchAccountData = state => getAccountDataStatus(state) === DATA_LOADING_STATES.NONE ||
    getAccountDataStatus(state) === DATA_LOADING_STATES.ERROR ||
    getAccountDataStatus(state) === DATA_LOADING_STATES.OBSOLETE;

const getIsModuleActive = state => state.isModuleActive;
const getUseBotsGraphMock = state => state.useBotsGraphMock;
const getBotsGraphMock = state => state.botsGraphMock;
const getBotsGraphUserSettings = (state) => {
  const botSettings = find(map(state.userSettings.widget_settings.settings, 'config'),
    widget => widget.i === WIDGET_LOGICAL_NAMES.BOTS);
  return {
    graphW: botSettings.graphW,
    widgetMode: botSettings.widgetMode,
  };
};

const getCurrentPresetName = state => state.userSettings.widget_settings.preset.currentPreset.name;

const getCurrentPresetDisplayName = (state) => {
  const currentName = getCurrentPresetName(state);
  let translatedDisplayName;
  if (currentName === PRESETS.DEFAULT_PRESET_NAME) {
    translatedDisplayName = i18n.t('amd:preset_default_name');
  } else {
    translatedDisplayName = currentName;
  }
  return translatedDisplayName;
};

const getPresetsList = state => state.userSettings.widget_settings.preset.presets;

const getCurrentPreset = (state) => {
  const currentPresetName = getCurrentPresetName(state);
  const allPresets = getPresetsList(state);
  return allPresets.find(item => item.name === currentPresetName);
};

const getPresetByName = state => (presetName) => {
  const allPresets = getPresetsList(state);
  return find(allPresets, item => item.name === presetName);
};

const getPresetsValidatedOnFirstLoad = state => state.presetsValidatedOnFirstLoad;

const getShowBotGraph = state => state.showBotGraph;

const getDialogAlertItems = state => state.dialogAlertItems;

const getAlertData = state => state.alertData;

const isWidgetCollapsed = state => (widgetName) => {
  const widgets = getVisibleWidgets(state);
  const widget = widgets.find(item => item.i === widgetName);
  return widget?.isCollapsed || false;
};

const getTabs = (state) => {
  const tabs = [{ id: DASHBOARD_TAB_ID, text: i18n.t('amd:tab_dashboard') }];
  let tabWidgets = remove(_getTabWidgetList(state), item => HIDDEN_WIDGET_TABS.indexOf(item.id) === -1);
  tabWidgets = tabWidgets.sort((a, b) => a.displayOrder - b.displayOrder);
  each(tabWidgets, (widget) => {
    const tabObj = {
      id: widget.id,
      text: i18n.t(`amd:${WIDGET_NAMES_I18N_KEYS[widget.id]}`),
      tooltip: i18n.t(`amd:tab_${widget.id}_tooltip`),
    };
    tabs.push(tabObj);
  });
  return tabs;
};

const getSelectedTabId = state => state.userSettings.widget_settings.selectedTabId;

const getWidgetConfig = state => (widgetName) => {
  const widgetConfig = find(map(state.userSettings.widget_settings.settings, 'config'),
    widget => widget.i === widgetName);
  return widgetConfig || {};
};

const getAlertPermissions = (state, getters) => {
  const isAlertFeatureEnabled = getters.getHasFeature(FEATURES.MANAGER_WORKSPACE_ALERTS);
  const hasSetAlertPermission = getters.getHasPrivilege(PRIVILEGES.CONFIGURE_ALERTS);
  const hasViewAlertPermission = getters.getHasPrivilege(PRIVILEGES.VIEW_ALERTS);
  return {
    canSetAlert: isAlertFeatureEnabled && hasSetAlertPermission,
    canViewAlert: isAlertFeatureEnabled && hasViewAlertPermission,
  };
};

const getAgentListUserSettings = (state) => {
  const agentSettings = find(map(state.userSettings.widget_settings.settings, 'config'),
    widget => widget.i === WIDGET_LOGICAL_NAMES.AGENTS);
  return { widgetMode: agentSettings.widgetMode };
};

const getCollapseToggleShown = state => state.userSettings.widget_settings.selectedTabId === DASHBOARD_TAB_ID;

const getIsWidgetCollapsed = state => (widgetName) => {
  const isCollapseShown = getCollapseToggleShown(state);
  const isCollapsed = isWidgetCollapsed(state)(widgetName);
  return isCollapseShown && isCollapsed;
};

export default {
  getVisibleWidgets,
  getWidgetValidMetrics,
  getWidgetParentMetrics,
  getWidgetDisplayedMetrics,
  getWidgetMetricAttributes,
  getSkills,
  getUsers,
  getAgentGroups,
  getStatusReasons,
  getBlockConsumerName,
  getCanConfigureManagerWorkspace,
  getMetricsConfiguration,
  getAgentGroupsUpdateRequired,
  getUserManaged,
  getUserManagedSkillsIds,
  getUserManagedSkills,
  getTimeFrameFilter,
  getGroupsFilterIds,
  getSkillsFilterIds,
  getGlobalFiltersUpdating,
  getUserManagedGroupsIds,
  getGroupsIds,
  getUserIds,
  getHumanUserIds,
  getFilteredGroupsUsersIds,
  getSkillsRevision,
  getUsersRevision,
  getAgentGroupsRevision,
  getUserManagedRevision,
  getStatusReasonsRevision,
  getMetricsConfigurationRevision,
  getAccountDataExists,
  getNewManagerApiState,
  getFetchResponseMetricsState,
  getNumericAmwVersion,
  getAmwVersion,
  getMetricsConfigurationVersion,
  getUserSettings,
  getAccountDataStatus,
  initDataStarted,
  getAccountDataSubscriptionsStatus,
  getWidgetsSubscriptionsStatus,
  useWorkspacePartialWidgets,
  getWidgetPermission,
  hasAgentProfileOnly,
  getSkillsFilterData,
  failedToFetchAccountData,
  getSiteSetting,
  getLoggedInUserId,
  getAccountId,
  getIsLPA,
  getHasPrivilege,
  getHasFeature,
  hasLiveIntent,
  hasLiveIntentsFilter,
  getIsModuleActive,
  getUseBotsGraphMock,
  getBotsGraphMock,
  getBotsGraphUserSettings,
  getCurrentPresetName,
  getCurrentPresetDisplayName,
  getCurrentPreset,
  getPresetsList,
  getPresetByName,
  getPresetsValidatedOnFirstLoad,
  getShowBotGraph,
  getUserIdsIgnoreFilter,
  getDialogAlertItems,
  getAlertData,
  getTabs,
  getSelectedTabId,
  getActiveWidgets,
  getWidgetConfig,
  getAlertPermissions,
  getAgentListUserSettings,
  getCollapseToggleShown,
  isWidgetCollapsed,
  getIsWidgetCollapsed,
};
